import React, { useCallback, useState } from 'react';
import PropTypes from 'prop-types';
import { formatRoute } from 'react-router-named-routes';
import { Box, Tooltip } from '@mui/material';
import { useNavigate } from 'react-router-dom';
import WarningIcon from '@mui/icons-material/Warning';
import { useSelector } from 'react-redux';
import { ActivityObjectType } from '../../API';
import SharedContentBoardActionsPopupMenu from './shared_content_board_actions_popup_menu';
import { SMORG_STUDIO_SPACE_SHARED_CONTENT_BOARD_ROUTE } from '../../services/routes';
import SharedContentBoardEditModal from './shared_content_board_edit_modal';
import { otherUserActiveOnResourceSelector } from '../../features/server_status/server_status_slice';
import { useGetOrCreateSpaceContentBoardsQuery } from '../../app/smorgAppSyncGraphQLApiCustomHooks';
import {
  useDeleteSharedContentEntryMutation,
  useGetSpaceQuery,
  useUpdateSharedContentBoardMutation,
  useUpdateSpaceMutation,
} from '../../app/smorgAppSyncGraphQLApi';
import { reportAccountActivitySignalOperation } from '../../operations/user_profile_operations';
import {
  currentUserSelector,
  userFriendlyNameForActivitySignalSelector,
} from '../../features/user_id/user_id_slice';

const SharedContentBoardNavigationBar = ({ sharedContentBoardID }) => {
  const navigate = useNavigate();

  const { data: space } = useGetSpaceQuery();

  const [updateSpace] = useUpdateSpaceMutation();

  const [deleteSharedContentEntry] = useDeleteSharedContentEntryMutation();

  const [editMode, setEditMode] = useState(false);

  const { data: sharedContentBoards } = useGetOrCreateSpaceContentBoardsQuery();

  const board = (sharedContentBoards || []).find(
    (b) => b.id === sharedContentBoardID,
  );
  const selectedRecipesBoardTitle = board?.title;

  const onEditBoard = () => {
    setEditMode(true);
  };

  const [updateSharedContentBoard] = useUpdateSharedContentBoardMutation();

  const friendlyUsername = useSelector(
    userFriendlyNameForActivitySignalSelector,
  );

  const updateBoard = useCallback(
    async (updatedBoard) => {
      setEditMode(false);
      await updateSharedContentBoard(updatedBoard);
      reportAccountActivitySignalOperation({
        spaceID: space.id,
        friendlyUsername,
        lastActivityObjectType: ActivityObjectType.SHARED_CONTENT_BOARDS,
        lastActivityObjectID: updatedBoard.id,
      });
    },
    [friendlyUsername, space?.id, updateSharedContentBoard],
  );

  const onDeleteBoard = async () => {
    const firstRemainingBoard = sharedContentBoards.find(
      (b) => b.id !== sharedContentBoardID,
    );
    const objectIDs = board.lanes
      .flatMap((l) => l.entries)
      .map((e) => e.objectID);
    await Promise.all((objectID) =>
      objectIDs.map(
        deleteSharedContentEntry({ id: objectID, parentID: board.id }),
      ),
    );
    const updatedSpace = {
      ...space,
      spaceSharedContentBoardIDs: (
        space.spaceSharedContentBoardIDs || []
      ).filter((cbid) => cbid !== sharedContentBoardID),
    };
    await updateSpace(updatedSpace);
    navigate(
      formatRoute(SMORG_STUDIO_SPACE_SHARED_CONTENT_BOARD_ROUTE, {
        sharedContentBoardID: firstRemainingBoard.id,
      }),
    );
  };

  const { username: myID } = useSelector(currentUserSelector);

  const otherActiveUser = useSelector((state) =>
    otherUserActiveOnResourceSelector(
      state,
      myID,
      ActivityObjectType.SHARED_CONTENT_BOARDS,
      sharedContentBoardID,
    ),
  );

  return (
    <Box
      display="flex"
      className="plannerNavigationBar"
      justifyContent="center"
      style={{ color: 'var(--app-primary-font-color)' }}>
      <div style={{ margin: 'auto 0', padding: '0 10px' }}>
        {selectedRecipesBoardTitle}
      </div>
      {otherActiveUser && (
        <div style={{ marginLeft: '20px', display: 'flex' }}>
          <Tooltip title="Modifying this board whilst another user is active may cause data loss">
            <WarningIcon
              style={{
                margin: 'auto 10px auto 0',
                color: 'orange',
              }}
            />
          </Tooltip>
          <div style={{ margin: 'auto 0', fontSize: '14px' }}>
            {otherActiveUser} is currently editing this board!
          </div>
        </div>
      )}
      <div style={{ flexGrow: 1, margin: 'auto 0' }}>
        <div
          style={{
            display: 'flex',
            justifyContent: 'space-around',
            margin: 'auto 0',
          }}
        />
      </div>
      <div style={{ padding: '0 10px' }}>
        <SharedContentBoardActionsPopupMenu
          sharedContentBoardID={sharedContentBoardID}
          deleteBoardDisabled={
            !sharedContentBoards || sharedContentBoards.length <= 1
          }
          onEditBoard={onEditBoard}
          onDeleteBoard={onDeleteBoard}
        />
      </div>
      {editMode && (
        <SharedContentBoardEditModal
          initialBoard={board}
          onSave={updateBoard}
          onDismiss={() => setEditMode(false)}
        />
      )}
    </Box>
  );
};

SharedContentBoardNavigationBar.propTypes = {
  sharedContentBoardID: PropTypes.string.isRequired,
};

export default SharedContentBoardNavigationBar;
